import { VFC } from "react";
import { Avatar, Badge, Button } from "antd";
import { useHistory } from "react-router-dom";
import { WarningOutlined } from "@ant-design/icons";

import { formatDate, formatRelativeTime } from "../i18n";
import { BusinessNotificationType, Notification, NotificationStatus, RoleSlug } from "../queries/api/types";
import { CheckOne, HourglassFull, Inbox, Message, OrderAdded, OrderCancelled } from "./icons";
import { useReadNotification } from "../queries/notifications";
import { getRoute, RoutePathName } from "../routes";
import { useAuth } from "../context/AuthContext";

interface NotificationCardProps {
    notification: Notification;
    closeNotifications: () => void;
}

const commentTypeIcon = (
    <Avatar size={32} className="!flex items-center justify-center !bg-brand-blue-600 text-base" icon={<Message />} />
);

const actionRequiredIcon = (
    <Avatar
        size={32}
        className="!flex items-center justify-center !bg-brand-red-600 text-base"
        icon={<HourglassFull />}
    />
);

const warningOrangeIcon = (
    <Avatar
        size={32}
        className="!flex items-center justify-center !bg-brand-orange-600 text-base"
        icon={<WarningOutlined />}
    />
);

const warningRedIcon = (
    <Avatar
        size={32}
        className="!flex items-center justify-center !bg-brand-red-600 text-base"
        icon={<WarningOutlined />}
    />
);

const tourOrderAddedIcon = (
    <Avatar
        size={32}
        className="!flex items-center justify-center !bg-brand-blue-600 text-base"
        icon={<OrderAdded />}
    />
);

const orderCanceledIcon = (
    <Avatar
        size={32}
        className="!flex items-center justify-center !bg-brand-blue-600 text-base"
        icon={<OrderCancelled />}
    />
);

const successGreenIcon = (
    <Avatar size={32} className="!flex items-center justify-center !bg-brand-green-600 text-base" icon={<CheckOne />} />
);

const getNotificationIcon = (
    businessNotificationType?: Notification["payload"]["custom"]["businessNotificationType"],
) => {
    switch (businessNotificationType) {
        case BusinessNotificationType.addCommentByOperator:
        case BusinessNotificationType.addCommentByShippingManager:
        case BusinessNotificationType.updateCommentByOperator:
        case BusinessNotificationType.updateCommentByShippingManager:
        case BusinessNotificationType.deleteCommentByOperator:
        case BusinessNotificationType.deleteCommentByShippingManager:
            return commentTypeIcon;

        case BusinessNotificationType.forcedPlatformPermissionRequest:
        case BusinessNotificationType.forcedTrailerPermission:
            return actionRequiredIcon;

        case BusinessNotificationType.forcedPlatformPermissionResponse:
            return <></>;

        case BusinessNotificationType.cancelDelivery:
        case BusinessNotificationType.declineDelivery:
            return warningRedIcon;

        case BusinessNotificationType.tourOrderAdded:
            return tourOrderAddedIcon;

        case BusinessNotificationType.cancelTourOrder:
            return orderCanceledIcon;

        case BusinessNotificationType.changePlatform:
        case BusinessNotificationType.driverHazardTaskRegister:
        case BusinessNotificationType.importTourOrdersStatusError:
            return warningOrangeIcon;

        case BusinessNotificationType.importTourOrdersStatusSuccess:
            return successGreenIcon;

        default:
            return <Avatar size={32} className="bg-brand-blue-600 text-base" icon={<Inbox />} />;
    }
};

const NotificationCard: VFC<NotificationCardProps> = ({ notification, closeNotifications }) => {
    const { user } = useAuth();
    const isShippingManager = user?.mainRole?.slug === RoleSlug.shippingManager;
    const history = useHistory();
    const { mutate: readNotification } = useReadNotification();

    const onClickNotification = () => {
        if (notification.status === NotificationStatus.received) {
            readNotification(notification.id);
        }
        closeNotifications();
        if (
            [
                BusinessNotificationType.addCommentByOperator,
                BusinessNotificationType.addCommentByShippingManager,
                BusinessNotificationType.updateCommentByOperator,
                BusinessNotificationType.updateCommentByShippingManager,
                BusinessNotificationType.deleteCommentByOperator,
                BusinessNotificationType.deleteCommentByShippingManager,
                BusinessNotificationType.forcedPlatformPermissionRequest,
                BusinessNotificationType.forcedTrailerPermission,
                BusinessNotificationType.cancelTourOrder,
                BusinessNotificationType.changePlatform,
                BusinessNotificationType.declineDelivery,
                BusinessNotificationType.tourOrderAdded,
            ].includes(notification.payload.custom.businessNotificationType) &&
            !isShippingManager
        ) {
            history.push(
                getRoute(RoutePathName.tourOrderDetails, { tourOrderId: notification.payload.custom.tourOrderId }),
            );
        }
    };

    return (
        <Button className="flex items-center gap-12" onClick={onClickNotification}>
            <div className="self-center">
                {getNotificationIcon(notification.payload.custom.businessNotificationType)}
            </div>
            <div>
                <div className="mb-4">{notification.payload.text.fr}</div>
                <time
                    dateTime={notification.sendAt}
                    title={formatDate(notification.sendAt, {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                    })}
                    className="text-xs text-brand-neutral-light-grey"
                >
                    {formatRelativeTime(notification.sendAt)}
                </time>
            </div>
            <div className="notification-status">
                {notification.status === NotificationStatus.received && <Badge color="#0066CC" dot />}
            </div>
        </Button>
    );
};
export default NotificationCard;
